import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGavel, faCalendarAlt, faTag, faArrowUp,
  faCheckCircle, faBan, faStore, faUser,
  faExclamationTriangle, faFlag,
  faEnvelope, faPhone, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import '../assets/css/legal-pages.css';

const AcceptableUsePolicy = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="legal-page">
      {/* Hero Header */}
      <div className="legal-header">
        <FontAwesomeIcon icon={faGavel} className="legal-header-icon" />
        <h1>Acceptable Use Policy</h1>
        <div className="legal-header-meta">
          <span><FontAwesomeIcon icon={faCalendarAlt} /> Effective Date: 6th March, 2026</span>
          <span><FontAwesomeIcon icon={faTag} /> Version 1.0</span>
        </div>
        <div className="legal-intro">
          This Acceptable Use Policy governs how users may access and use the AutoboyExpress platform, website, and mobile application. By accessing or using AutoboyExpress, you agree to comply with this policy. Violation of this policy may result in immediate account suspension or permanent termination.
        </div>
      </div>

      {/* 1. Introduction */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faGavel} /> 1. Introduction</h2>
        <p>AutoboyExpress is a Nigerian gadget marketplace platform that connects buyers and sellers for the buying, selling, and swapping of gadgets. This Acceptable Use Policy defines the rules and standards that all users — buyers, sellers, and visitors — must follow when using the platform.</p>
        <p>This policy exists to protect the integrity of the platform, ensure a safe and trusted trading environment, and comply with applicable Nigerian laws and regulations.</p>
      </div>

      {/* 2. Permitted Use */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faCheckCircle} /> 2. Permitted Use</h2>
        <p>Users may use AutoboyExpress only for the following permitted purposes:</p>
        <ul>
          <li>Listing genuine gadgets for sale or swap in accurate condition as described.</li>
          <li>Purchasing or swapping gadgets listed on the platform.</li>
          <li>Submitting swap offers through the platform's structured swap offer form.</li>
          <li>Submitting legitimate swap offers with accurate device information and photographs.</li>
          <li>Raising genuine disputes through the platform's dispute resolution process.</li>
          <li>Managing your account, listings, orders, and profile within the platform.</li>
        </ul>
      </div>

      {/* 3. Prohibited Items */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBan} /> 3. Prohibited Items</h2>
        <p>The following items are strictly prohibited from being listed or traded on AutoboyExpress:</p>
        <ul>
          <li>Stolen, counterfeit, or fraudulently obtained gadgets.</li>
          <li>Gadgets with tampered, cloned, or altered IMEI numbers.</li>
          <li>Gadgets that are reported lost or stolen on any national or international database.</li>
          <li>Unlicensed or illegally imported gadgets.</li>
          <li>Gadgets that have been modified to bypass security features, carrier locks, or regional restrictions fraudulently.</li>
          <li>Any item that is not a gadget or gadget accessory — AutoboyExpress is strictly a gadget marketplace.</li>
          <li>Hazardous or recalled electronic devices.</li>
        </ul>
      </div>

      {/* 4. Prohibited Behaviour */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBan} /> 4. Prohibited Behaviour</h2>
        <p>The following behaviours are strictly prohibited on AutoboyExpress:</p>

        <h3>4.1 Fraud and Misrepresentation</h3>
        <ul>
          <li>Listing gadgets with false, misleading, or exaggerated descriptions or photographs.</li>
          <li>Impersonating another user, business, or AutoboyExpress staff.</li>
          <li>Submitting fraudulent swap offers with false device information.</li>
          <li>Creating multiple accounts to manipulate ratings, reviews, or platform policies.</li>
          <li>Engaging in any form of identity fraud during the KYC verification process.</li>
        </ul>

        <h3>4.2 Payment and Financial Misconduct</h3>
        <ul>
          <li>Attempting to make or receive payments outside of the AutoboyExpress platform.</li>
          <li>Using stolen, cloned, or unauthorised payment methods.</li>
          <li>Attempting to manipulate, circumvent, or exploit the escrow system.</li>
          <li>Engaging in money laundering or using the platform to process proceeds of crime.</li>
          <li>Conducting transactions designed to evade Nigerian financial regulations or tax obligations.</li>
        </ul>

        <h3>4.3 Communication Policy Violations</h3>
        <ul>
          <li>Sharing personal contact details — phone numbers, email addresses, social media handles, or any external contact information — through any part of the platform including listings, swap offer forms, ratings, or reviews.</li>
          <li>Attempting to conduct transactions or negotiations outside of the AutoboyExpress platform. All negotiations must happen exclusively through the structured swap offer form.</li>
          <li>Sending spam, unsolicited messages, or promotional content to other users.</li>
          <li>Using abusive, threatening, or harassing language in any platform communication.</li>
        </ul>

        <h3>4.4 Platform Integrity Violations</h3>
        <ul>
          <li>Attempting to hack, reverse engineer, or tamper with the AutoboyExpress platform or its systems.</li>
          <li>Using bots, scripts, or automated tools to manipulate listings, searches, or platform activity.</li>
          <li>Submitting false, malicious, or retaliatory reviews or ratings.</li>
          <li>Attempting to access another user's account without authorisation.</li>
          <li>Uploading malicious software, viruses, or harmful code to the platform.</li>
        </ul>

        <h3>4.5 Legal and Regulatory Violations</h3>
        <ul>
          <li>Using the platform for any purpose that violates Nigerian law or any applicable international law.</li>
          <li>Engaging in or facilitating human trafficking, terrorism financing, or any other criminal activity.</li>
          <li>Violating the intellectual property rights of any third party.</li>
          <li>Using the platform to engage in any activity that constitutes a criminal offence under Nigerian law.</li>
        </ul>
      </div>

      {/* 5. Seller Obligations */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faStore} /> 5. Seller-Specific Obligations</h2>
        <p>In addition to the general rules above, all sellers on AutoboyExpress must:</p>
        <ul>
          <li>Provide accurate, honest, and complete descriptions and photographs of all listed gadgets.</li>
          <li>Disclose all known faults, repairs, and modifications in the listing.</li>
          <li>Ensure the gadget is available and in the described condition at the time of sale or swap.</li>
          <li>Complete KYC verification including NIN, CAC (for business sellers), and BVN verification before listing products.</li>
          <li>Hand over gadgets to FabsTech Express riders promptly upon receiving an order notification.</li>
          <li>Not attempt to contact the buyer directly outside of the platform at any point.</li>
        </ul>
      </div>

      {/* 6. Buyer Obligations */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faUser} /> 6. Buyer-Specific Obligations</h2>
        <p>In addition to the general rules above, all buyers on AutoboyExpress must:</p>
        <ul>
          <li>Provide accurate delivery address information at the time of checkout.</li>
          <li>Raise disputes only for genuine, valid reasons within the 3-day dispute window.</li>
          <li>Not attempt to contact the seller directly outside of the platform at any point.</li>
          <li>Not attempt to manipulate or abuse the dispute or refund process.</li>
        </ul>
      </div>

      {/* 7. Consequences */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faExclamationTriangle} /> 7. Consequences of Violations</h2>
        <p>AutoboyExpress reserves the right to take the following actions against users who violate this policy:</p>
        <ul>
          <li>Immediate suspension of the user's account pending investigation.</li>
          <li>Permanent termination of the user's account.</li>
          <li>Removal of all listings associated with the violating account.</li>
          <li>Withholding of escrowed funds pending investigation where financial misconduct is suspected.</li>
          <li>Reporting of the violation to relevant Nigerian law enforcement authorities including the EFCC, NFIU, and Nigerian Police where applicable.</li>
          <li>Legal action where the violation constitutes a criminal offence under Nigerian law.</li>
        </ul>
      </div>

      {/* 8. Reporting */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faFlag} /> 8. Reporting Violations</h2>
        <p>If you become aware of any user violating this Acceptable Use Policy, please report it to us immediately. All reports are treated confidentially and investigated promptly by our admin team.</p>
        <p>Email: <a href="mailto:support@autoboyexpress.com">support@autoboyexpress.com</a></p>
      </div>

      {/* 9. Amendments */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faGavel} /> 9. Amendments</h2>
        <p>AutoboyExpress reserves the right to update or amend this Acceptable Use Policy at any time. Users will be notified of material changes via email or in-app notification. Continued use of the platform after any amendment constitutes acceptance of the updated policy.</p>
      </div>

      {/* Contact */}
      <div className="legal-contact">
        <h2><FontAwesomeIcon icon={faEnvelope} /> 10. Contact Us</h2>
        <div className="legal-contact-grid">
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
            <span>3/9 Olukolesho Street, Tayeolu Tower, Lagos, Nigeria</span>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
            <a href="mailto:support@autoboyexpress.com">support@autoboyexpress.com</a>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faPhone} className="contact-icon" />
            <a href="tel:09136941527">09136941527</a> / <a href="tel:08110749313">08110749313</a>
          </div>
        </div>
      </div>

      <div className="legal-back-top">
        <button onClick={scrollToTop}>
          <FontAwesomeIcon icon={faArrowUp} /> Back to Top
        </button>
      </div>
    </div>
  );
};

export default AcceptableUsePolicy;
